import React, { useState } from 'react';
import { Reservation } from '../../types';
import { ReservationModificationLog, ApiReservation } from '../../services/reservationService';
import { PaymentRecord } from '../../services/paymentService';
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency, formatStatus } from '../../utils/formatters';
import {
  PaymentAction,
  PaymentStatusActionButtons,
  PaymentStatusConfirmModal,
  statusUpdateResultText,
} from './PaymentStatusUpdate';

export interface ModificationHistorySectionProps {
  isModifiedReservation: boolean;
  modLogs: ReservationModificationLog[];
  modLogsLoading: boolean;
  oldReservationMap: Record<number, ApiReservation>;
  modLogsExpanded: boolean;
  setModLogsExpanded: (expanded: boolean) => void;
  paymentsByTransactionId: Map<string, PaymentRecord>;
  /** When true, uses CSS classes from ReservationPopup.css. When false, uses inline styles. */
  useClassStyles?: boolean;
  /** When provided, manual charge payments get status-correction actions. */
  reservation?: Reservation;
  /** Called after a payment status is changed, so the host can refetch payment data. */
  onPaymentsChanged?: () => void;
}

const formatSummaryValue = (key: string, value: any): string => {
  if (key === 'check_in') return String(value).split('T')[0];
  if (key === 'cost') return formatCurrency(Number(value));
  return String(value);
};

const renderPaymentRow = (
  label: string,
  payment: PaymentRecord,
  isRefund: boolean,
  useClassStyles: boolean,
  actions?: React.ReactNode
) => {
  if (useClassStyles) {
    return (
      <div key={`${label}-${payment.id}`} className="mod-log-payment-row">
        <span>{label}: {formatStatus(payment.payment_type || payment.payment_method || 'payment')}</span>
        <span className={`mod-log-payment-amount${isRefund ? ' refund' : ''}`}>
          {isRefund ? '-' : ''}{formatCurrency(payment.amount)}
        </span>
        <span>{formatStatus(payment.status)}</span>
        <span>{payment.processed_at ? formatDate(payment.processed_at) : payment.created_at ? formatDate(payment.created_at) : '—'}</span>
        {actions}
      </div>
    );
  }

  return (
    <div
      key={`${label}-${payment.id}`}
      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.75rem', gap: '0.5rem', flexWrap: 'wrap' }}
    >
      <span>{label}: {formatStatus(payment.payment_type || payment.payment_method || 'payment')}</span>
      <span style={{ fontWeight: 600, color: isRefund ? '#dc2626' : undefined }}>
        {isRefund ? '-' : ''}{formatCurrency(payment.amount)}
      </span>
      <span>{formatStatus(payment.status)}</span>
      <span>{payment.processed_at ? formatDate(payment.processed_at) : payment.created_at ? formatDate(payment.created_at) : '—'}</span>
      {actions}
    </div>
  );
};

const ModificationHistorySection: React.FC<ModificationHistorySectionProps> = ({
  isModifiedReservation,
  modLogs,
  modLogsLoading,
  oldReservationMap,
  modLogsExpanded,
  setModLogsExpanded,
  paymentsByTransactionId,
  useClassStyles = false,
  reservation,
  onPaymentsChanged,
}) => {
  const [pendingAction, setPendingAction] = useState<{ payment: PaymentRecord; action: PaymentAction } | null>(null);
  const [resultText, setResultText] = useState<string | null>(null);

  if (!isModifiedReservation) return null;

  // Status-correction actions for manual charge payments (never refunds)
  const chargeActions = (payment: PaymentRecord): React.ReactNode => {
    if (!reservation) return undefined;
    return (
      <PaymentStatusActionButtons
        payment={payment}
        reservationStatus={reservation.status}
        hideMessage
        onAction={(p, action) => setPendingAction({ payment: p, action })}
      />
    );
  };

  const statusModal = pendingAction && (
    <PaymentStatusConfirmModal
      payment={pendingAction.payment}
      action={pendingAction.action}
      onClose={() => setPendingAction(null)}
      onUpdated={(response) => {
        setPendingAction(null);
        setResultText(statusUpdateResultText(response));
        if (!response.no_op) {
          onPaymentsChanged?.();
        }
      }}
    />
  );

  const resultBanner = resultText && (
    <p style={{ fontSize: '0.8rem', color: '#065f46', margin: '0 0 0.5rem 0' }}>{resultText}</p>
  );

  if (useClassStyles) {
    // CSS class-based rendering (ReservationPopup context)
    return (
      <section className="popup-section">
        <h3
          className="section-title mod-log-toggle"
          onClick={() => setModLogsExpanded(!modLogsExpanded)}
        >
          <span className={`toggle-arrow ${modLogsExpanded ? 'expanded' : ''}`}>▶</span>
          Modification History
          {modLogs.length > 0 && <span className="mod-log-count">{modLogs.length}</span>}
        </h3>
        {modLogsExpanded && (
          <div className="modification-history">
            {resultBanner}
            {modLogsLoading ? (
              <p className="payment-loading">Loading modification history...</p>
            ) : modLogs.length === 0 ? (
              <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>No modification logs found.</p>
            ) : (
              modLogs.map((log) => {
                const changeSummary = typeof log.change_summary === 'object' && log.change_summary
                  ? log.change_summary
                  : {};
                const oldRes = oldReservationMap[log.old_reservation_id];
                const oldSiteAlias = oldRes?.reservation_sites?.[0]?.site?.alias;

                const oldValueMap: Record<string, any> = oldRes ? {
                  check_in: oldRes.check_in?.split('T')[0],
                  days: oldRes.days,
                  adult: oldRes.adult,
                  child: oldRes.child,
                  cost: oldRes.cost,
                  special_requests: oldRes.special_requests,
                  status: oldRes.status,
                } : {};

                const paymentTx = log.payment_transaction_id
                  ? paymentsByTransactionId.get(log.payment_transaction_id)
                  : undefined;
                const refundTx = log.refund_transaction_id
                  ? paymentsByTransactionId.get(log.refund_transaction_id)
                  : undefined;

                return (
                  <div key={log.id} className="mod-log-entry">
                    <div className="mod-log-header">
                      <span className="mod-log-type">{formatStatus(log.modification_type)}</span>
                      <span className="mod-log-date">
                        {log.modified_at ? formatDate(log.modified_at) : '—'}
                      </span>
                    </div>
                    {(Object.keys(changeSummary).length > 0 || oldSiteAlias) && (
                      <div className="mod-log-changes">
                        {oldSiteAlias && (
                          <span className="mod-log-change">
                            <strong>Previous Site:</strong> {oldSiteAlias}
                          </span>
                        )}
                        {Object.entries(changeSummary).map(([key, value]) => {
                          const prevValue = oldValueMap[key];
                          const newFormatted = formatSummaryValue(key, value);
                          const prevFormatted = prevValue != null ? formatSummaryValue(key, prevValue) : null;
                          const changed = prevFormatted != null && prevFormatted !== newFormatted;
                          return (
                            <span key={key} className="mod-log-change">
                              <strong>{formatStatus(key)}:</strong>{' '}
                              {changed
                                ? <>{prevFormatted} → {newFormatted}</>
                                : newFormatted
                              }
                            </span>
                          );
                        })}
                      </div>
                    )}
                    <div className="mod-log-footer">
                      <span>Cost Difference: {formatCurrency(log.cost_difference)}</span>
                      {log.notes && <span className="mod-log-notes">{log.notes}</span>}
                    </div>
                    {(paymentTx || refundTx) && (
                      <div className="mod-log-payments">
                        <div className="mod-log-payments-title">Payments</div>
                        {paymentTx && renderPaymentRow('Charge', paymentTx, false, true, chargeActions(paymentTx))}
                        {refundTx && renderPaymentRow('Refund', refundTx, true, true)}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
        {statusModal}
      </section>
    );
  }

  // Inline-style rendering (MemberCard / CustomerCard context)
  return (
    <div className="detail-requests">
      <label
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setModLogsExpanded(!modLogsExpanded)}
      >
        <span style={{ display: 'inline-block', transition: 'transform 0.2s', transform: modLogsExpanded ? 'rotate(90deg)' : 'rotate(0deg)', marginRight: '0.25rem' }}>&#9654;</span>
        Modification History
        {modLogs.length > 0 && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', background: 'var(--primary)', color: '#fff', borderRadius: '10px', padding: '0 0.5rem' }}>{modLogs.length}</span>}
      </label>
      {modLogsExpanded && (
        <div style={{ marginTop: '0.5rem' }}>
          {resultBanner}
          {modLogsLoading ? (
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Loading modification history...</p>
          ) : modLogs.length === 0 ? (
            <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>No modification logs found.</p>
          ) : (
            modLogs.map((log) => {
              const changeSummary = typeof log.change_summary === 'object' && log.change_summary
                ? log.change_summary
                : {};
              const oldRes = oldReservationMap[log.old_reservation_id];
              const oldSiteAlias = oldRes?.reservation_sites?.[0]?.site?.alias;

              const oldValueMap: Record<string, any> = oldRes ? {
                check_in: oldRes.check_in?.split('T')[0],
                days: oldRes.days,
                adult: oldRes.adult,
                child: oldRes.child,
                cost: oldRes.cost,
                special_requests: oldRes.special_requests,
                status: oldRes.status,
              } : {};

              const paymentTx = log.payment_transaction_id
                ? paymentsByTransactionId.get(log.payment_transaction_id)
                : undefined;
              const refundTx = log.refund_transaction_id
                ? paymentsByTransactionId.get(log.refund_transaction_id)
                : undefined;

              return (
                <div key={log.id} style={{ borderLeft: '3px solid var(--primary)', paddingLeft: '0.75rem', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '0.25rem' }}>
                    <span style={{ fontWeight: 600 }}>{formatStatus(log.modification_type)}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{log.modified_at ? formatDate(log.modified_at) : '—'}</span>
                  </div>
                  {(Object.keys(changeSummary).length > 0 || oldSiteAlias) && (
                    <div style={{ fontSize: '0.8rem' }}>
                      {oldSiteAlias && (
                        <div><strong>Previous Site:</strong> {oldSiteAlias}</div>
                      )}
                      {Object.entries(changeSummary).map(([key, value]) => {
                        const prevValue = oldValueMap[key];
                        const newFormatted = formatSummaryValue(key, value);
                        const prevFormatted = prevValue != null ? formatSummaryValue(key, prevValue) : null;
                        const changed = prevFormatted != null && prevFormatted !== newFormatted;
                        return (
                          <div key={key}>
                            <strong>{formatStatus(key)}:</strong>{' '}
                            {changed
                              ? <>{prevFormatted} → {newFormatted}</>
                              : newFormatted
                            }
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                    Cost Difference: {formatCurrency(log.cost_difference)}
                    {log.notes && <span style={{ marginLeft: '0.5rem' }}>— {log.notes}</span>}
                  </div>
                  {(paymentTx || refundTx) && (
                    <div style={{ borderTop: '1px dashed var(--border)', marginTop: '0.25rem', paddingTop: '0.25rem' }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.15rem' }}>Payments</div>
                      {paymentTx && renderPaymentRow('Charge', paymentTx, false, false, chargeActions(paymentTx))}
                      {refundTx && renderPaymentRow('Refund', refundTx, true, false)}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
      {statusModal}
    </div>
  );
};

export default ModificationHistorySection;
